import React from 'react';
import { Link } from 'react-router-dom';
import { FaAngleLeft } from 'react-icons/fa';
import Swal from 'sweetalert2';

import { browserHistory } from '../../../browserHistory';

import { settings } from '../../../settings';
import Fetch from '../../../utils/fetch';
import Button, { ButtonGroup } from '../../../components/buttons/Button';
import { unique } from '../../../utils/array';
import {
  BundleJson,
  BundleStatus,
  InstructionType,
} from '../../../types/bundleTypes';

interface State {
  cancellingOrder: boolean;
}

interface Props {
  bundle: BundleJson;
  onShowCourierModal: () => void;
  onChangeBundleShelf: () => void;
  onAddOrder: () => void;
  onMarkAsPickedUp: () => void;
  onMarkAsDelivered: () => void;
  onRemoveOrders: () => void;
  onCancelSelectMode: () => void;
  onConfirmSelectMode: () => void;
  onCompleteBundle: () => void;
  confirmOrdersButton: boolean;
  confirmOrdersButtonText: string;
  onCancelModalChange: (status: boolean) => void;
  onRescheduleOrders: () => void;
  onResetPickups: () => void;
}

class BundleMenuBar extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      cancellingOrder: false,
    };
  }

  static canCancelBundle(bundle: BundleJson) {
    if (bundle.instructions.length === 0) {
      return false;
    }

    switch (bundle.status) {
      case BundleStatus.Draft:
      case BundleStatus.Published:
      case BundleStatus.Proposed:
      case BundleStatus.Accepted:
        return true;
      case BundleStatus.Completed:
      case BundleStatus.Deleted:
        return false;
      default:
        return true;
    }
  }

  cancelBundle = async (bundle: BundleJson) => {
    this.setState({ cancellingOrder: true });

    const response = await Fetch.put({
      url: `${settings.newApiUrl}/bundle/${bundle.id}/cancel`,
    });

    this.setState({ cancellingOrder: false }, () =>
      this.props.onCancelModalChange(false)
    );

    if (response.ok) {
      Swal.fire(
        'Success!',
        `Bundle ${this.props.bundle.id} is cancelled`,
        'success'
      )
        .then(() => browserHistory.push('/bundles'))
        .catch(error => console.error(error.message));
    } else {
      Swal.fire('Bundle could not be cancelled', undefined, 'error');
    }
  };

  render() {
    const {
      bundle,
      onShowCourierModal,
      onChangeBundleShelf,
      onAddOrder,
      onMarkAsPickedUp,
      onMarkAsDelivered,
      onRemoveOrders,
      onRescheduleOrders,
      onCancelSelectMode,
      onConfirmSelectMode,
      confirmOrdersButton,
      confirmOrdersButtonText,
      onCompleteBundle,
      onCancelModalChange,
      onResetPickups,
    } = this.props;

    const orderCount: number = unique(
      bundle.instructions.map(instruction => instruction.orderId)
    ).length;

    return (
      <div>
        <Link
          className="link__clean"
          style={{ display: 'flex', alignItems: 'center', width: 200 }}
          to="/monitor"
        >
          <FaAngleLeft style={{ width: 20, height: 20, color: '#3a3d4b' }} />
          Back to bundles
        </Link>
        <div className="bundle__menu-bar">
          {!confirmOrdersButton && (
            <>
              <ButtonGroup>
                <Button
                  title={
                    bundle.assignedCourierId
                      ? 'Reassign Courier'
                      : 'Assign Courier'
                  }
                  onClick={onShowCourierModal}
                  disabled={[
                    BundleStatus.Completed,
                    BundleStatus.Deleted,
                  ].includes(bundle.status)}
                />
                <Button
                  title={'Add Order'}
                  type={'default'}
                  onClick={onAddOrder}
                  disabled={bundle.status === BundleStatus.Deleted}
                />
                <Button
                  title={'Remove Orders'}
                  type={'default'}
                  onClick={onRemoveOrders}
                  disabled={
                    bundle.status === BundleStatus.Deleted || orderCount === 0
                  }
                />
                <Button
                  title={bundle.shelfId ? 'Change Shelf' : 'Assign Shelf'}
                  type={'default'}
                  onClick={onChangeBundleShelf}
                  disabled={[
                    BundleStatus.Completed,
                    BundleStatus.Deleted,
                    BundleStatus.Draft,
                  ].includes(bundle.status)}
                />
              </ButtonGroup>
              <ButtonGroup>
                <Button
                  title={'Mark as Picked Up'}
                  type={'default'}
                  onClick={onMarkAsPickedUp}
                  disabled={
                    bundle.status === BundleStatus.Deleted || orderCount === 0
                  }
                />
                <Button
                  title={'Mark as Delivered'}
                  type={'default'}
                  onClick={onMarkAsDelivered}
                  disabled={
                    bundle.status === BundleStatus.Deleted || orderCount === 0
                  }
                />
                <Button
                  title={'Reset Pickups'}
                  type={'default'}
                  onClick={onResetPickups}
                  disabled={bundle.instructions.some(
                    instruction =>
                      instruction.type === InstructionType.Delivery &&
                      instruction.completed
                  )}
                />
              </ButtonGroup>
              <ButtonGroup>
                <Button
                  title={'Shift to new delivery date'}
                  type={'default'}
                  onClick={onRescheduleOrders}
                  disabled={
                    bundle.status === BundleStatus.Deleted || orderCount === 0
                  }
                />
                <Button
                  title={'Complete Bundle'}
                  type={'default'}
                  onClick={onCompleteBundle}
                  disabled={
                    bundle.status === BundleStatus.Deleted ||
                    bundle.status === BundleStatus.Completed
                  }
                />
              </ButtonGroup>
            </>
          )}
          {confirmOrdersButton && (
            <>
              <ButtonGroup>
                <Button
                  title={confirmOrdersButtonText}
                  type={'success'}
                  onClick={() => onConfirmSelectMode()}
                />
                <Button
                  title={'Cancel'}
                  type={'default'}
                  onClick={() => onCancelSelectMode()}
                />
              </ButtonGroup>
            </>
          )}
          <div className="mobile-only" style={{ height: 10 }} />
          <div>
            {!confirmOrdersButton && (
              <Button
                title="Cancel bundle"
                type="danger"
                disabled={!BundleMenuBar.canCancelBundle(bundle)}
                loading={this.state.cancellingOrder}
                onClick={() => {
                  onCancelModalChange(true);
                  Swal.fire({
                    title: 'Danger zone!',
                    text: `Are you sure you want to cancel bundle ${bundle.id}? This is a irreversible action. All the orders in the bundle will change their status to Ready. If there is any couriers currently delivering this bundle, they will lose this bundle.`, // eslint-disable-line max-len
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Cancel bundle',
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#ccc',
                  })
                    .then(result => {
                      if (result.value) {
                        this.cancelBundle(bundle);
                      } else {
                        Swal.fire('The bundle is safe!');
                        onCancelModalChange(false);
                      }
                    })
                    .catch(error => console.error(error.message));
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default BundleMenuBar;
